import React, { useEffect, useMemo, useState, useRef } from 'react';
import styles from './NavigationMenu.module.css';
import { useGSAP } from '@/hooks/useGSAP';
import { MenuLink, SocialLinkMenu } from '@/components/atoms';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

export const NavigationMenu = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [currentPath, setCurrentPath] = useState('');
  const { gsap, ready } = useGSAP();
  const linkedinLink =
    import.meta.env.VITE_LINKEDIN_LINK ||
    'https://fr.linkedin.com/company/lnr-agency';

  const instagramLink =
    import.meta.env.VITE_INSTAGRAM_LINK ||
    'https://www.instagram.com/lnr.agency/';

  const socialsInfos = [
    {
      name: 'Instagram',
      link: instagramLink,
    },
    {
      name: 'LinkedIn',
      link: linkedinLink,
    },
  ];

  const routes = useMemo(
    () => [
      { name: t('common.navigation.agency'), route: '/' },
      { name: t('common.navigation.cases'), route: '/cases' },
      { name: t('common.navigation.codex'), route: '/codex' },
      { name: t('common.navigation.contact'), route: '/contact' },
    ],
    [t]
  );

  // Handle word click in WordStack
  const handleWordClick = (_word: string, index: number) => {
    const route = routes[index]?.route || '/';

    if (location.pathname === route) {
      onClose();
      return;
    }

    onClose();

    setTimeout(() => {
      navigate(route);
    }, 300);
  };

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!ready || !gsap || !containerRef.current) return;

    gsap.to(containerRef.current, {
      autoAlpha: isOpen ? 1 : 0,
      opacity: isOpen ? 1 : 0,
      duration: 0.4,
      ease: 'power2.out',
      pointerEvents: isOpen ? 'auto' : 'none',
    });
  }, [isOpen, ready, gsap]);

  return (
    <div ref={containerRef} className={styles.menuOverlay}>
      <nav className={styles.menuContent}>
        {routes.map((route, index) => {
          const isActive =
            currentPath.toLowerCase() === route.route.toLowerCase();

          return (
            <MenuLink
              key={route.name}
              index={index}
              label={route.name}
              isActive={isActive}
              isOpen={isOpen}
              onClick={() => handleWordClick(route.route, index)}
            />
          );
        })}
      </nav>

      <footer className={styles.socials}>
        {socialsInfos.map((s, index) => (
          <SocialLinkMenu
            key={s.name}
            label={s.name}
            href={s.link}
            isOpen={isOpen}
            index={index}
          />
        ))}
      </footer>
    </div>
  );
};
